import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * R-029a (C02-F2) — the id both TCS predictive-search sections put on their
 * results root: sections/tcs-predictive-search.liquid (results, no-match and
 * the recently-viewed sink) and sections/tcs-predictive-search-empty.liquid
 * (idle). A section-rendering response that does not carry it is not a panel
 * render, whatever its status code.
 */
const TCS_RESULTS_ROOT_ID = 'predictive-search-results';

/**
 * Whether a section-rendering response carries the TCS results root.
 *
 * Measured 2026-09-21 (Claude, branch fix/a11y-c02-f2-predictive-search-20260921)
 * against origin/main 5145d2b in Chromium 153.0.8010.12 and WebKit 26.6:
 * `sectionRenderer.getSectionHTML()` resolves with the response body whatever
 * the status, so a 500 or 429 error document reaches the `.then` branch as a
 * resolved string; `morph()` then parses its first body node into the panel and
 * the panel is left with 0 result items, 0 `[role="status"]` regions and 0
 * `input[name="type"]` fields. Regression:
 * scripts/tests/tcs-predictive-search-failure.mjs.
 *
 * @param {unknown} markup - The section-rendering response body.
 * @returns {boolean} True when the response carries the TCS results root.
 */
function hasResultsRoot(markup) {
  if (typeof markup !== 'string' || markup.trim() === '') return false;
  return (
    new DOMParser().parseFromString(markup, 'text/html').getElementById(TCS_RESULTS_ROOT_ID) != null
  );
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  #searchVersion = 0;

  // R-029a (C02-F2): true while the panel's status region is showing
  // `tcs.search.error` rather than a server-rendered result count.
  #searchErrorShown = false;

  // The handles for the staged live-region error update. Safari + VoiceOver did
  // not reliably announce the 2026-09-21 candidate when the same visible text
  // was rewritten in the same input session.
  #searchErrorFrame = null;

  #searchErrorTimer = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    // R-013: TCS renders native links, not a virtual-focus listbox. In
    // particular, the debounced search/reset path must not steal Tab focus.
    if (this.dataset.sectionId === 'tcs-predictive-search') return;
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    // R-013: the TCS search dialog uses native Tab/Shift+Tab and form/link
    // activation. Escape belongs to DialogComponent. Do not assign selected
    // states to list items or keep DOM focus in the input while navigating.
    if (this.dataset.sectionId === 'tcs-predictive-search') return;
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    const searchVersion = this.#searchVersion;
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm, searchVersion);
  }, 200);

  onSearchInput = (event) => {
    if (event.inputType) {
      this.#searchVersion += 1;
      this.#activeFetch?.abort();
      this.#clearSearchError();
    }
    this.search(event);
  };

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   * @param {number} searchVersion - The input version this request belongs to
   */
  async #getSearchResults(searchTerm, searchVersion) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url, abortController.signal)
      .then((resultsMarkup) => {
        // R-047 (follow-up to R-029a, C02-F2): no early return on an empty body.
        // section-renderer returns response.text() whatever the status, so an
        // empty 500 arrives here as '' and must reach the failure path below; a
        // stale or aborted request still resolves silently first.
        if (abortController.signal.aborted || searchVersion !== this.#searchVersion) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        // R-029a (C02-F2): a response that does not carry the TCS results root
        // is a failure — do NOT morph. Morphing an error document empties the
        // panel and takes the form's hidden type=product field with it, which
        // drops Enter back into the all-types /search of the 2026-09-03
        // dead-end bug (see sections/tcs-predictive-search.liquid). Keeping the
        // current content keeps that field. The status line is the panel's own
        // existing live region; no retry control, no second live
        // region. Adding a response.ok check to assets/section-renderer.js is
        // refused by R-029a and reported upstream instead (docs/horizon.md).
        if (!hasResultsRoot(resultsMarkup)) {
          this.#showSearchError();
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        this.#clearSearchError();

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        if (abortController.signal.aborted || searchVersion !== this.#searchVersion) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }
        // R-029a (C02-F2): the network path. The panel is left as it stands,
        // so the previous query's results no longer sit silently under new
        // input — the status region says the suggestions did not load. The
        // `throw error` that used to close this handler is removed: it turned
        // every offline keystroke into an unhandled rejection. The promise is
        // resolved, not rejected, because the failure is handled here; otherwise
        // the storefront's real SearchUpdateEvent promise becomes the unhandled
        // rejection Safari reported at this line.
        this.#showSearchError();
        deferredPromise.resolve({ totalCount: 0, didError: true });
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  /**
   * The panel's existing live region. Both TCS sections render exactly one,
   * as the first child of `.predictive-search-results__inner`, so the node a
   * failure writes into is the same node a later successful render morphs the
   * result count into — the region is never added or removed, only retexted.
   * During a visible failure it is promoted from status to alert so Safari +
   * VoiceOver treats a repeated failure as a fresh assertive announcement.
   * @returns {HTMLElement | null} The live region, or null if the panel has none.
   */
  get #statusRegion() {
    return this.refs.predictiveSearchResults.querySelector('[role="status"], [role="alert"]');
  }

  /**
   * R-029a (C02-F2): put `tcs.search.error` in the panel's live region.
   * The string arrives on one `data-` attribute from snippets/search-modal.liquid,
   * matching the snippets/tcs-head.liquid pattern; `layout/theme.liquid` and
   * `Theme.translations` are untouched. Without the attribute or the region
   * this is a no-op, so a panel rendered without either degrades to the
   * pre-R-029a silence rather than to a thrown error.
   */
  #showSearchError() {
    const message = this.#decodeSearchError(this.dataset.searchError || '');
    const status = this.#statusRegion;

    if (!message || !status) return;

    status.classList.remove('visually-hidden');
    status.classList.add('tcs-predictive__status--error');
    status.setAttribute('role', 'alert');
    status.setAttribute('aria-live', 'assertive');
    status.textContent = '';
    this.#searchErrorShown = true;

    if (this.#searchErrorFrame !== null) cancelAnimationFrame(this.#searchErrorFrame);
    if (this.#searchErrorTimer !== null) clearTimeout(this.#searchErrorTimer);
    this.#searchErrorFrame = requestAnimationFrame(() => {
      this.#searchErrorFrame = null;
      this.#searchErrorTimer = setTimeout(() => {
        this.#searchErrorTimer = null;
        if (this.#searchErrorShown) status.textContent = message;
      }, 80);
    });
  }

  /**
   * Drop a shown error before the panel is morphed, so a recovery never leaves
   * the old failure text behind for the instant before the new markup lands.
   */
  #clearSearchError() {
    if (!this.#searchErrorShown) return;

    this.#searchErrorShown = false;

    if (this.#searchErrorFrame !== null) {
      cancelAnimationFrame(this.#searchErrorFrame);
      this.#searchErrorFrame = null;
    }
    if (this.#searchErrorTimer !== null) {
      clearTimeout(this.#searchErrorTimer);
      this.#searchErrorTimer = null;
    }

    const status = this.#statusRegion;
    if (status) {
      status.textContent = '';
      status.classList.add('visually-hidden');
      status.classList.remove('tcs-predictive__status--error');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
    }
  }

  /**
   * Shopify's translation escape can arrive double-encoded in data attributes
   * on the password-protected preview (`couldn&#39;t` was visibly rendered in the
   * 2026-09-21 Safari check). Decode only into textContent, never into HTML.
   * @param {string} value
   * @returns {string}
   */
  #decodeSearchError(value) {
    if (typeof document === 'undefined') {
      return value
        .replace(/&#39;|&apos;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&');
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'tcs-predictive-search-empty';

    this.#searchVersion += 1;
    this.#currentIndex = -1;
    this.#clearSearchError();
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
